import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Card,
  DatePicker,
  Upload,
  message,
} from "antd";
import { LeftOutlined, InboxOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  getLicensorsPartnersList,
  raisePartnerTaxInvoiceRequest,
  getPartnerTaxInvoiceData,
  editTaxInvoiceRequest,
  getSingleTaxRequestData,
  editRoyaltyInvoiceRequestData,
  deleteInvoiceRequestData,
} from "../../../appRedux/actions";
import moment from "moment";

const { TextArea } = Input;
const { Dragger } = Upload;

const { Option } = Select;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const AddTaxData = () => {
  const { editTaxRequestData, royaltyPartnersList } = useSelector(
    ({ partnerInvoice }) => partnerInvoice,
  );
  const [editFlag, setEditFlag] = useState(false);
  const [editedFields, setEditedFields] = useState({});

  const handleFieldChange = (changedValues) => {
    setEditedFields((prevEditedFields) => ({
      ...prevEditedFields,
      ...changedValues,
    }));
  };

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const navigateToPartnerList = () => {
    navigate("/partner/partner-invoice");
  };

  useEffect(() => {
    // Set initial values when editTaxRequestData changes
    if (editFlag && editTaxRequestData) {
      form.setFieldsValue({
        partner: editTaxRequestData?.partner_id?.partner_id,
        year: editTaxRequestData?.year
          ? moment(`${editTaxRequestData?.year}-01-01T00:00:00.000Z`)
          : null,
        tax_invoice: editTaxRequestData?.invoice_file?.map((file) => ({
          uid: file.id,
          name: file.document.split("/").pop(),
          status: "done",
          url: file.document,
        })),
      });
    }
  }, [editFlag, editTaxRequestData, form]);

  const handleRemove = (file) => {
    if (file.url) {
      dispatch(deleteInvoiceRequestData(file.uid));
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const checkFile = (file) => {
    const allowedFileTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const isTypeValid = allowedFileTypes.includes(file.type);
    const isSizeValid = file.size / 1024 / 1024 <= 15;

    if (!isTypeValid) {
      message.error("Please upload a PDF, JPEG, or PNG document!");
    } else if (!isSizeValid) {
      message.error("File size must be less than or equal to 15 MB!");
    }

    return isTypeValid && isSizeValid;
  };

  const fetchData = async () => {
    await dispatch(getLicensorsPartnersList());

    // Check if editing
    if (params.id && location.pathname.includes("edit-request")) {
      await dispatch(getSingleTaxRequestData(params.id));
      setEditFlag(true);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateBack = () => {
    navigate(-1);
  };

  const onFinish = (values) => {
    const formData = new FormData();
    if (!editFlag) {
      values.tax_invoice.forEach((data) => {
        formData.append("tax_invoice", data.originFileObj);
      });
      formData.append("year", moment(values.year.toISOString())?.year());
      formData.append("partner_id", values.partner);
      dispatch(raisePartnerTaxInvoiceRequest(formData, navigateBack));
    } else {
      Object.entries(editedFields).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Filter files that have originFileObj
          const filesWithOriginFileObj = value.filter(
            (file) => file.originFileObj,
          );
          filesWithOriginFileObj.forEach((file) => {
            formData.append(`${key}`, file.originFileObj);
          });
        } else {
          if (key === "year") {
            formData.append("year", moment(value.toISOString())?.year());
          } else {
            formData.append(key, value);
          }
        }
      });
      formData.append("status", "PENDING");
      dispatch(editTaxInvoiceRequest(params.id, formData, navigateBack));
    }
  };

  return (
    <div>
      <Row>
        <Col span={24}>
          <h1 className="title gx-mb-4">
            <LeftOutlined className="mr-5" onClick={navigateBack} />
            {editFlag ? "Edit Tax Invoice Data" : "Add Tax Document Data"}
          </h1>
        </Col>
        <Col span={24}>
          <Card className="gx-card">
            <Row>
              <Col span={16}>
                <Form
                  {...formItemLayout}
                  form={form}
                  name="add-request"
                  onValuesChange={handleFieldChange}
                  onFinish={onFinish}
                  initialValues={{
                    partner: editTaxRequestData?.partner_id?.partner_id,
                    year: editTaxRequestData?.year
                      ? moment(`${editTaxRequestData?.year}-01-01T00:00:00.000Z`)
                      : null,
                    tax_invoice: editTaxRequestData?.invoice_file?.map((file) => ({
                      uid: file.id,
                      name: file.document.split("/").pop(),
                      status: "done",
                      url: file.document,
                    })),
                  }}
                  scrollToFirstError
                >
                  <Form.Item
                    label="Year"
                    name="year"
                    rules={[
                      {
                        required: true,
                        message: "Please select a year!",
                      },
                    ]}
                  >
                    <DatePicker picker="year" />
                  </Form.Item>

                  <Form.Item
                    label="Partner"
                    name="partner"
                    rules={[
                      {
                        required: true,
                        message: "Please select a partner!",
                      },
                    ]}
                  >
                    <Select placeholder="Select a partner">
                      {royaltyPartnersList.map((partner) => (
                        <Option
                          key={partner.partner_id}
                          value={partner.partner_id}
                        >
                          {partner.company_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Document"
                    name="tax_invoice"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const fileList = getFieldValue("tax_invoice");

                          if (!fileList) {
                            return Promise.reject(
                              new Error("Please upload a file!"),
                            );
                          }

                          const allowedFileTypes = [
                            "application/pdf",
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                          ];
                          const isFileTypeValid = fileList.every(
                            (file) =>
                              !file.originFileObj ||
                              allowedFileTypes.includes(file.type),
                          );

                          if (!isFileTypeValid) {
                            return Promise.reject(
                              new Error(
                                "Please upload a PDF, JPEG, or PNG document!",
                              ),
                            );
                          }

                          const isSizeValid = fileList.every(
                            (file) =>
                              !file.originFileObj ||
                              (allowedFileTypes.includes(file.type) &&
                                file.size / 1024 / 1024 <= 15),
                          );

                          if (!isSizeValid) {
                            return Promise.reject(
                              new Error(
                                "File size must be less than or equal to 15 MB!",
                              ),
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Dragger
                      name="tax_invoice"
                      multiple={true}
                      beforeUpload={checkFile}
                      accept=".pdf, .png, .jpeg, .jpg"
                      onRemove={handleRemove}
                      customRequest={({ file, onSuccess }) => {
                        onSuccess();
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag files to this area to upload
                      </p>
                      <p className="ant-upload-hint">
                        Support for a single or bulk upload (pdf, png, jpeg).
                      </p>
                    </Dragger>
                  </Form.Item>

                  <Form.Item className="flex-d-row-reverse">
                    <Button
                      id="add-partner-save"
                      type="primary"
                      htmlType="submit"
                    >
                      {editFlag ? "Save" : "Submit Request"}
                    </Button>
                    <Button
                      id="add-partner-cancel"
                      onClick={navigateToPartnerList}
                    >
                      Cancel
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
              <Col span={2}></Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AddTaxData;
